import React, { useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { Navigate, useLocation, useNavigate, Link } from "react-router-dom";
// Role check is now based on instructor_profiles table, not role field
import { useViewAs } from "../contexts/ViewAsContext";
import { instructorAccessService } from "../services/instructorAccessService";
import { getAuthenticationRedirectUrl } from "../utils/redirectUtils";
import {
  Clock,
  ArrowLeft,
  LogIn,
  AlertTriangle,
  GraduationCap,
  Mail,
  CheckCircle,
  Loader2,
  ShieldAlert,
} from "lucide-react";

const ProtectedInstructorRoute = ({ children }) => {
  const { user, loading: authLoading, userProfile, profileLoading } = useAuth();
  const { isViewingAs, effectiveRole } = useViewAs();
  const location = useLocation();
  const navigate = useNavigate();

  const getInitialCheck = () => {
    if (isViewingAs) {
      if (effectiveRole === "instructor") {
        return { hasAccess: true, loading: false, error: null, pending: false };
      }
      return {
        hasAccess: false,
        loading: false,
        error: "Instructor privileges required",
        pending: false,
      };
    }
    return { hasAccess: false, loading: true, error: null, pending: false };
  };

  const [check, setCheck] = useState(getInitialCheck);

  useEffect(() => {
    const verifyInstructorAccess = async () => {
      if (authLoading || profileLoading) return;

      if (isViewingAs) {
        if (effectiveRole === "instructor") {
          setCheck({
            hasAccess: true,
            loading: false,
            error: null,
            pending: false,
          });
        } else {
          setCheck({
            hasAccess: false,
            loading: false,
            error: "Instructor privileges required",
            pending: false,
          });
        }
        return;
      }

      if (!user) {
        setCheck({
          hasAccess: false,
          loading: false,
          error: "Authentication required",
          pending: false,
        });
        return;
      }

      // Check instructor_profiles directly instead of role field
      // This supports dual-role users (partner + instructor)
      try {
        const { profile: data, partnerApproved, partnerAdmin } =
          await instructorAccessService.getAccess(user.id);

        let result;
        if (data?.approval_status === "rejected") {
          result = {
            hasAccess: false,
            loading: false,
            error: "rejected",
            pending: false,
            reason: data.rejection_reason || null,
          };
        } else if (data?.approval_status === "suspended") {
          result = {
            hasAccess: false,
            loading: false,
            error: "suspended",
            pending: false,
            reason: data.rejection_reason || null,
          };
        } else if (data?.approval_status === "approved") {
          result = {
            hasAccess: true,
            loading: false,
            error: null,
            pending: false,
            reason: null,
          };
        } else if (!data) {
          // Course selection, batch creation and quiz access all require an
          // approved platform profile, so a partner-active instructor with
          // no profile is sent to create one rather than into an empty
          // dashboard.
          result = partnerApproved || partnerAdmin
            ? {
                hasAccess: false,
                loading: false,
                error: null,
                pending: false,
                needsProfile: true,
                reason: null,
              }
            : {
                hasAccess: false,
                loading: false,
                error: "Instructor privileges required",
                pending: false,
                reason: null,
              };
        } else {
          result = {
            hasAccess: false,
            loading: false,
            error: null,
            pending: true,
            reason: null,
          };
        }

        setCheck(result);
      } catch (error) {
        console.error("Error checking instructor access:", error);
        setCheck({
          hasAccess: false,
          loading: false,
          error: "Access verification failed",
          pending: false,
        });
      }
    };

    verifyInstructorAccess();
  }, [
    user,
    authLoading,
    profileLoading,
    userProfile,
    isViewingAs,
    effectiveRole,
  ]);

  // ── Loading ────────────────────────────────────────────────
  if (authLoading || profileLoading || check.loading) {
    return (
      <div className="min-h-screen bg-dm-page relative overflow-hidden flex items-center justify-center">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-emerald-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-blue-100/40 rounded-full blur-3xl" />
        <div className="relative z-10 text-center">
          <div className="w-12 h-12 border-[3px] border-slate-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 font-medium text-sm">
            Verifying instructor access...
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    const currentPath = `${location?.pathname || "/"}${location?.search || ""}`;
    const authUrl = getAuthenticationRedirectUrl(currentPath);
    return <Navigate to={authUrl} replace />;
  }

  if (check.needsProfile) {
    return <Navigate to="/instructor/onboarding" replace />;
  }

  // ── Pending approval ───────────────────────────────────────
  if (check.pending) {
    return (
      <div className="min-h-screen bg-dm-page relative overflow-hidden flex items-center justify-center p-4">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-amber-100/40 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-emerald-100/40 rounded-full blur-3xl" />

        <div className="relative z-10 w-full max-w-lg">
          <div className="bg-dm-surface/80 backdrop-blur-xl border border-gray-200/60 dark:border-dm-border rounded-3xl shadow-xl dark:shadow-none p-10">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-amber-500" />
              </div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">
                Application Under Review
              </h2>
              <p className="text-slate-500 font-medium">
                Your instructor profile has been submitted and is being reviewed
                by a platform admin.
              </p>
            </div>

            {/* Status timeline */}
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 mb-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      Profile submitted
                    </p>
                    <p className="text-xs text-slate-500">
                      Your application has been received
                    </p>
                  </div>
                </div>

                <div className="ml-3.5 border-l-2 border-dashed border-amber-200 h-4" />

                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-amber-100 rounded-full flex items-center justify-center shrink-0 mt-0.5 ring-2 ring-amber-200 ring-offset-1">
                    <Loader2 className="w-4 h-4 text-amber-600 animate-spin" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-700">
                      Under review
                    </p>
                    <p className="text-xs text-slate-500">
                      An admin is reviewing your profile
                    </p>
                  </div>
                </div>

                <div className="ml-3.5 border-l-2 border-dashed border-slate-200 h-4" />

                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                    <GraduationCap className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-400">
                      Approved
                    </p>
                    <p className="text-xs text-slate-400">
                      Access your instructor dashboard
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info box */}
            <div className="bg-blue-50/80 border border-blue-100 rounded-2xl p-4 mb-8">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <p className="text-sm text-blue-700 font-medium">
                  You'll receive an email notification once your profile is
                  approved. This usually takes 1-2 business days.
                </p>
              </div>
            </div>

            {/* Action */}
            <button
              onClick={() => navigate("/")}
              className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Browse Courses
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Access denied ──────────────────────────────────────────
  if (!check.hasAccess) {
    const isRejected = check.error === "rejected";
    const isSuspended = check.error === "suspended";

    return (
      <div className="min-h-screen bg-dm-page relative overflow-hidden flex items-center justify-center p-4">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-red-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-orange-100/30 rounded-full blur-3xl" />

        <div className="relative z-10 w-full max-w-md">
          <div className="bg-dm-surface/80 backdrop-blur-xl border border-gray-200/60 dark:border-dm-border rounded-3xl shadow-xl dark:shadow-none p-10 text-center">
            <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              {isRejected || isSuspended ? (
                <ShieldAlert className="w-10 h-10 text-red-500" />
              ) : (
                <AlertTriangle className="w-10 h-10 text-red-500" />
              )}
            </div>

            <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">
              {isRejected
                ? "Application Not Approved"
                : isSuspended
                  ? "Account Suspended"
                  : "Access Denied"}
            </h2>
            <p className="text-slate-500 font-medium mb-4">
              {isRejected
                ? "Your instructor application was not approved."
                : isSuspended
                  ? "Your instructor account has been suspended."
                  : "You need instructor privileges to access this page."}
            </p>

            {(isRejected || isSuspended) && check.reason && (
              <div className="bg-red-50/60 border border-red-100 rounded-2xl p-4 mb-6 text-left">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-red-600 mb-1">
                  Reason from admin
                </p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {check.reason}
                </p>
              </div>
            )}

            {(isRejected || isSuspended) && (
              <p className="text-xs text-slate-400 mb-8">
                If you think this was a mistake, reply to your approval email or
                contact support.
              </p>
            )}

            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate("/")}
                className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-lg flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </button>
              {!user && (
                <Link
                  to="/login"
                  state={{ from: location }}
                  className="w-full py-3 text-slate-600 hover:text-slate-900 font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedInstructorRoute;
